import { z } from 'zod';

import { httpClient } from '@/shared/api/httpClient';

import {
  campaignDetailSchema,
  campaignPreviewSchema,
  campaignReconcileResultSchema,
  campaignSummarySchema,
} from '../model/types';
import type {
  CampaignDetail,
  CampaignPreview,
  CampaignReconcileResult,
  CampaignSummary,
  CreateCampaignInput,
} from '../model/types';

/**
 * Czyste funkcje endpointów — zero Reacta, zero stanu.
 *
 * Każda odpowiedź przechodzi przez `zod`: rozjazd kontraktu ma wybuchać tam, gdzie powstał,
 * a nie trzy komponenty dalej.
 *
 * ⚠ **Rzutowanie na `AsContract<…>` przy każdym `parse`** zdejmuje jeden artefakt typów, a nie
 * sprawdzenie: `zod` przy `.optional()` dokłada `undefined` do **wartości** pola, a kontrakt
 * dopuszcza wyłącznie **brak klucza**. W danych różnicy nie ma — serwer te pola zawsze wysyła,
 * a opcjonalne są w dokumencie tylko dlatego, że rekord po stronie .NET-a ma wartości domyślne
 * w konstruktorze (patrz `AsContract`). Walidacja kształtu dzieje się **przed** rzutowaniem.
 */

export async function fetchCampaigns(signal?: AbortSignal): Promise<CampaignSummary[]> {
  const response = await httpClient.get<unknown>('/campaigns', { ...(signal ? { signal } : {}) });

  return z.array(campaignSummarySchema).parse(response) as CampaignSummary[];
}

export async function fetchCampaign(id: number, signal?: AbortSignal): Promise<CampaignDetail> {
  return campaignDetailSchema.parse(
    await httpClient.get<unknown>(`/campaigns/${id}`, { ...(signal ? { signal } : {}) }),
  ) as CampaignDetail;
}

/** Kogo obejmie uruchomienie — <b>zero zapisu</b>. */
export async function fetchPreview(id: number, signal?: AbortSignal): Promise<CampaignPreview> {
  return campaignPreviewSchema.parse(
    await httpClient.get<unknown>(`/campaigns/${id}/preview`, { ...(signal ? { signal } : {}) }),
  );
}

export async function createCampaign(input: CreateCampaignInput): Promise<CampaignSummary> {
  return campaignSummarySchema.parse(
    await httpClient.post<unknown>('/campaigns', input),
  ) as CampaignSummary;
}

export async function launchCampaign(id: number): Promise<CampaignDetail> {
  return campaignDetailSchema.parse(
    await httpClient.post<unknown>(`/campaigns/${id}/launch`, {}),
  ) as CampaignDetail;
}

/**
 * Uzgodnienie — dosypuje zadania elementom, które doszły do zbioru (decyzja 86).
 *
 * ⚠ To <b>POST</b>, mimo że ekran woła je „przy okazji" wejścia. Zapis ukryty w odczycie
 * znaczyłby, że odświeżenie strony albo prefetch <b>tworzy ludziom robotę</b>.
 */
export async function reconcileCampaign(id: number): Promise<CampaignReconcileResult> {
  return campaignReconcileResultSchema.parse(
    await httpClient.post<unknown>(`/campaigns/${id}/reconcile`, {}),
  );
}

export async function closeCampaign(id: number): Promise<CampaignDetail> {
  return campaignDetailSchema.parse(
    await httpClient.post<unknown>(`/campaigns/${id}/close`, {}),
  ) as CampaignDetail;
}
